// @mui material components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import Paper from "@mui/material/Paper";
import TableContainer from "@mui/material/TableContainer";

import Card from "@mui/material/Card";
// Soft UI Dashboard React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { apiurl } from "utils";
import Select2Field from 'components/Select2Field';
import Loading from 'components/Loading';
import debounce from 'debounce';
import Swal from 'sweetalert2';
import api from 'services/api';
import { useTranslate } from "utils/i18n";
import {
    figmaTokens,
    directoryTableHeaderCellSx,
    directoryTableHeadStickyOverridesSx,
    directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import { Grid, Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import { getUserRoleBucket } from "assistant/userRole";
import AccountStatusTabs from "components/AccountStatusTabs";
import {
    appendUserCreationFields,
    backendValidationMessage,
    normalizeCreatableRoles,
    normalizeIdCsv,
    userCreationAuthHeaders,
} from "utils/userCreation";
import {
    accountIsActive,
    apiErrorMessage,
    canChangeAccountStatus,
    getManagedUserDetail,
    getManagedUsers,
    normalizeUserEditValues,
    setManagedUserStatus,
} from "services/accountManagementService";


const API = {
    provinces: `${apiurl}Assignment/provinces`,
    departments: (provid) =>
        `${apiurl}Assignment/departments?provid=${provid}`,
    subprefectures: (dptid) =>
        `${apiurl}Assignment/subprefectures?dptid=${dptid}`,
    establishments: (sptid) =>
        `${apiurl}Assignment/establishments?sptid=${sptid}`,
};

// ✅ Utility to make options
const makeOptions = (data, ids, labels) =>
    data.map((item) => ({
        id: ids.map((key) => item[key]).find(Boolean), // pick first existing id
        text: labels.map((key) => item[key]).find(Boolean), // pick first label
    }));

/** Normalizes API casing (ASP.NET often sends PascalCase). */
function getUserRowRole(row) {
    return row?.userRole ?? row?.UserRole ?? row?.role ?? row?.Role ?? "";
}

/** Maps API userRole to stat / chip bucket */
const bucketUserRole = getUserRoleBucket;

function userRoleChipSx(bucket) {
    const base = { fontWeight: 600, fontSize: "0.72rem", height: 26, borderRadius: "8px" };
    switch (bucket) {
        case "admin":
            return { ...base, bgcolor: "#5b21b6", color: "#fff" };
        case "member":
            return { ...base, bgcolor: "rgba(102, 126, 234, 0.16)", color: figmaTokens.accent };
        case "observer":
            return { ...base, bgcolor: "rgba(245, 158, 11, 0.2)", color: "#b45309" };
        case "auth":
            return { ...base, bgcolor: figmaTokens.pageBg, color: figmaTokens.textSecondary, border: `1px solid ${figmaTokens.borderSubtle}` };
        default:
            return { ...base, bgcolor: "rgba(100, 116, 139, 0.14)", color: figmaTokens.textSecondary };
    }
}

const usersStatWrapSx = {
    minWidth: 0,
    height: "100%",
    display: "flex",
    "& > .MuiCard-root": {
        flex: 1,
        minHeight: 108,
        borderRadius: "16px",
        border: `1px solid ${figmaTokens.borderSubtle}`,
        boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
        backgroundColor: figmaTokens.surface,
        backgroundImage: `linear-gradient(145deg, rgba(102, 126, 234, 0.07) 0%, ${figmaTokens.surface} 52%)`,
        overflow: "hidden",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
            borderColor: "rgba(102, 126, 234, 0.28)",
            boxShadow: "0 16px 42px rgba(102, 126, 234, 0.12)",
            transform: "translateY(-2px)",
        },
        "& > div": {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
        },
    },
};

const usersTableShellSx = {
    mx: { xs: 1.25, md: 2 },
    mb: 0,
    borderRadius: "14px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    overflow: "auto",
    maxHeight: { xs: "65vh", md: "min(68vh, 680px)" },
    bgcolor: figmaTokens.surface,
    boxShadow: "0 10px 40px rgba(15, 23, 42, 0.07)",
};

const usersDialogPaperProps = {
    sx: {
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${figmaTokens.borderSubtle}`,
        boxShadow: "0 24px 64px rgba(15, 23, 42, 0.14)",
    },
};

const userEditFieldLabelSx = {
    display: "block",
    mb: 0.75,
    fontWeight: 700,
    fontSize: "0.68rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: figmaTokens.textMuted,
};

/** SweetAlert2 — align with figmaTokens (purple CTAs, neutral cancel on confirms) */
const swalApp = {
    confirmButtonColor: figmaTokens.accent,
    background: figmaTokens.surface,
    color: figmaTokens.textPrimary,
};

const swalDangerConfirm = {
    confirmButtonColor: figmaTokens.badgeDanger,
    cancelButtonColor: figmaTokens.textMuted,
    background: figmaTokens.surface,
    color: figmaTokens.textPrimary,
};


function Register() {
    const [provOpts, setProvOpts] = useState([]);
    const [deptOpts, setDeptOpts] = useState([]);
    const [creatableRoles, setCreatableRoles] = useState([]);
    const [rolesError, setRolesError] = useState("");
    const [users, setUsers] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [summary, setSummary] = useState({ total: 0, active: 0, inactive: 0 });
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const rowsPerPage = 25;
    const [edit, setEdit] = useState(false);
    const [editData, setEditData] = useState(null);
    const [openModel, setOpenModel] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const { t } = useTranslate();

    const roleOptions = useMemo(
        () => creatableRoles.map((role) => ({
            id: role,
            text: role === "Admin"
                ? t("users.role_admin", "Administrateur")
                : t("users.role_observator", "Observateur"),
        })),
        [creatableRoles, t]
    );


    const handleClose = () => {
        setOpenModel(false);
    };


    // Cache to avoid duplicate calls
    const cache = useRef({
        provinces: null,
        departments: {},
        subprefectures: {},
        establishments: {},
    });

    // Track controllers to cancel old requests
    const controllers = useRef({});

    const cancelRequest = (key) => {
        if (controllers.current[key]) {
            controllers.current[key].abort();
        }
        controllers.current[key] = new AbortController();
        return controllers.current[key].signal;
    };

    // Fetch provinces
    const fetchProvinces = useCallback(async () => {
        if (cache.current.provinces) {
            return setProvOpts(cache.current.provinces);
        }
        try {
            const { data } = await axios.get(API.provinces, {
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`
                }
            });
            const opts = makeOptions(data, ["provid", "id"], ["name", "province"]);
            cache.current.provinces = opts;
            setProvOpts(opts);
        } catch (err) {
            console.error("Fetch provinces error", err);
        }
    }, []);

    const fetchCreatableRoles = useCallback(async () => {
        setRolesError("");
        try {
            const { data } = await api.get("/Auth/creatable-roles", {
                headers: userCreationAuthHeaders(),
            });
            const roles = normalizeCreatableRoles(data);
            setCreatableRoles(roles);
            if (!roles.length) {
                setRolesError(t("users.roles_empty", "No roles are available for creation."));
            }
        } catch (error) {
            setCreatableRoles([]);
            setRolesError(backendValidationMessage(
                error,
                t("users.roles_load_error", "Unable to load the roles you may create.")
            ));
        }
    }, [t]);

    // Fetch departments for one or more provinces and return the normalized options.
    const loadDepartments = useCallback(
        async (provinceValue) => {
            const provinceIds = normalizeIdCsv(provinceValue).split(",").filter(Boolean);
            if (!provinceIds.length) {
                setDeptOpts([]);
                return [];
            }
            try {
                setLoading(true);
                const signal = cancelRequest("departments");
                const groups = await Promise.all(provinceIds.map(async (provid) => {
                    if (cache.current.departments[provid]) return cache.current.departments[provid];
                    const { data } = await axios.get(API.departments(provid), {
                        signal,
                        headers: { "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}` }
                    });
                    const opts = makeOptions(data, ["dptid", "id"], ["name", "department"]);
                    cache.current.departments[provid] = opts;
                    return opts;
                }));
                const unique = new Map(groups.flat().map((option) => [String(option.id), option]));
                const options = Array.from(unique.values());
                setDeptOpts(options);
                return options;
            } catch (err) {
                if (err.name !== "CanceledError") console.error("Fetch departments error", err);
                return [];
            } finally {
                setLoading(false);
            }
        },
        []
    );

    // Debounced loading keeps ordinary multi-select changes responsive.
    const fetchDepartments = useMemo(
        () => debounce((provinceValue) => loadDepartments(provinceValue), 300),
        [loadDepartments]
    );

    // Fetch teachers data from API or any other source
    const fetchUserList = async (requestedPage = page, requestedStatus = statusFilter) => {
        setLoading(true);
        try {
            const result = await getManagedUsers({
                status: requestedStatus,
                pageNumber: requestedPage,
                pageSize: rowsPerPage,
            });
            setUsers(result.items);
            setSummary(result.summary);
            setTotalRecords(result.pagination.totalRecords);
        } catch (err) {
            console.error("API Error:", err);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchProvinces();
        fetchCreatableRoles();
    }, [fetchCreatableRoles, fetchProvinces]);

    useEffect(() => {
        setPage(1);
        fetchUserList(1, statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);



    const formik = useFormik({
        initialValues: {
            // username: "",
            email: "",
            password: "",
            confirmPassword: "",
            firstName: "",
            lastName: "",
            title: "",
            gender: "",
            phone: "",
            whastappNo: "",
            role: "",
            Provinces: [],
            Departments: [],
        },
        validationSchema: Yup.object({
            // username: Yup.string().required("Nom d'utilisateur est requis"),
            email: Yup.string()
                .email(t("validation.email_invalid", "Email invalide"))
                .required(t("validation.email_required", "Email est requis")),
            // password: Yup.string().min(6, "Le mot de passe doit comporter au moins 6 caractères").required("Mot de passe est requis"),
            password: Yup.string()
                .required(t("validation.password_required", "Mot de passe est requis"))
                .min(8, t("validation.password_min", "Le mot de passe doit comporter au moins 8 caractères"))
                .matches(/[a-z]/, t("validation.password_lowercase", "Le mot de passe doit contenir au moins une lettre minuscule ('a'-'z')"))
                .matches(/[A-Z]/, t("validation.password_uppercase", "Le mot de passe doit contenir au moins une lettre majuscule ('A'-'Z')"))
                .matches(/[^a-zA-Z0-9]/, t("validation.password_special", "Le mot de passe doit contenir au moins un caractère non alphanumérique")),
            confirmPassword: Yup.string()
                .oneOf([Yup.ref("password"), null], t("validation.password_match", "Les mots de passe doivent correspondre"))
                .required(t("validation.confirm_password_required", "Confirmation du mot de passe est requise")),
            firstName: Yup.string().required(t("validation.first_name_required", "Prénom est requis")),
            lastName: Yup.string().required(t("validation.last_name_required", "Nom de famille est requis")),
            title: Yup.string().max(100, t("users.title_max", "Le titre ne peut pas dépasser 100 caractères")),
            gender: Yup.string().required(t("validation.gender_required", "Genre est requis")),
            phone: Yup.string().required(t("validation.phone_required", "Numéro de téléphone est requis")),
            role: Yup.string()
                .oneOf(["Admin", "Observer"], t("validation.role_invalid", "Select a valid role"))
                .required(t("validation.role_required", "Rôle est requis")),
            Provinces: Yup.array()
                .min(1, t("validation.province_required", "Province est requis"))
                .required(t("validation.province_required", "Province est requis")),
            Departments: Yup.array()
                .min(1, t("validation.department_required", "Département est requis"))
                .required(t("validation.department_required", "Département est requis")),
        }),
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            let shouldResetForm = false;
            try {
                console.log("Form submitted:", JSON.stringify(values));
                setLoading(true);
                const formData = new FormData();
                appendUserCreationFields(formData, values, provOpts, deptOpts);

                const response = await axios.post(apiurl + "Auth/register", formData, {
                    headers: userCreationAuthHeaders()
                });

                const responseErrors = Array.isArray(response?.data?.errors) ? response.data.errors : [];
                const usernameTakenInResponse = responseErrors.some(
                    (message) =>
                        typeof message === "string" &&
                        message.toLowerCase().includes("username") &&
                        message.toLowerCase().includes("already taken")
                );

                if (usernameTakenInResponse) {
                    Swal.fire({
                        ...swalApp,
                        icon: 'error',
                        title: t("users.create_error_title", "Erreur"),
                        text: "Nom d'utilisateur deja pris.",
                    });
                } else if (responseErrors.length > 0) {
                    Swal.fire({
                        ...swalApp,
                        icon: 'error',
                        title: t("users.create_error_title", "Erreur"),
                        text: responseErrors[0],
                    });
                } else {
                    Swal.fire({
                        ...swalApp,
                        icon: 'success',
                        title: t("users.create_success_title", "Utilisateur cree"),
                        text: t("users.create_success_text", "L'utilisateur a ete cree avec succes."),
                        showConfirmButton: false,
                        timer: 2000
                    });
                    fetchUserList();
                    shouldResetForm = true;
                }
            } catch (error) {
                console.error("Error creating utilisateur:", error);
                const errorList = Array.isArray(error?.response?.data?.errors) ? error.response.data.errors : [];
                const usernameTakenInError = errorList.some(
                    (message) =>
                        typeof message === "string" &&
                        message.toLowerCase().includes("username") &&
                        message.toLowerCase().includes("already taken")
                );

                if (usernameTakenInError || error?.response?.status === 409) {
                    Swal.fire({
                        ...swalApp,
                        icon: 'error',
                        title: t("users.create_error_title", "Erreur"),
                        text: "Nom d'utilisateur deja pris.",
                    });
                } else {
                    Swal.fire({
                        ...swalApp,
                        icon: 'error',
                        title: t("users.create_error_title", "Erreur"),
                        text: backendValidationMessage(
                            error,
                            t("users.create_error_text", "Une erreur s'est produite lors de la creation de l'utilisateur.")
                        ),
                    });
                }
            } finally {
                setLoading(false);
                setSubmitting(false);
                if (shouldResetForm) {
                    resetForm();
                }
            }
        },
    });

    const selectAllCreateGeography = async () => {
        const provinceIds = provOpts
            .map((option) => String(option.id ?? ""))
            .filter(Boolean);
        formik.setFieldValue("Provinces", provinceIds);
        formik.setFieldTouched("Provinces", true, false);
        formik.setFieldValue("Departments", []);
        const departments = await loadDepartments(provinceIds);
        const departmentIds = departments
            .map((option) => String(option.id ?? ""))
            .filter(Boolean);
        formik.setFieldValue("Departments", departmentIds);
        formik.setFieldTouched("Departments", true, false);
    };

    const selectAllCreateDepartments = () => {
        formik.setFieldValue(
            "Departments",
            deptOpts.map((option) => String(option.id ?? "")).filter(Boolean)
        );
        formik.setFieldTouched("Departments", true, false);
    };

    const editFormik = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            title: "",
            gender: "",
            phone: "",
            whastappNo: "",
            Provinces: "",
            Departments: "",
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required(t("validation.first_name_required", "Prénom est requis")),
            lastName: Yup.string().required(t("validation.last_name_required", "Nom de famille est requis")),
            title: Yup.string().max(100, t("users.title_max", "Le titre ne peut pas dépasser 100 caractères")),
            gender: Yup.string().required(t("validation.gender_required", "Genre est requis")),
            phone: Yup.string().required(t("validation.phone_required", "Numéro de téléphone est requis")),
            // Provinces: Yup.string().required("Province est requis"),
            // Departments: Yup.string().required("Département est requis"),
        }),
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                console.log("Form submitted:", JSON.stringify(values));
                setLoading(true);
                const formData = new FormData();
                formData.append("FirstName", values.firstName);
                formData.append("LastName", values.lastName);
                formData.append("Title", values.title.trim());
                formData.append("Gender", values.gender);
                formData.append("Phone", values.phone);
                formData.append("WhastappNo", values.whastappNo);
                formData.append("Provinces", normalizeIdCsv(values.Provinces));
                formData.append("Departments", normalizeIdCsv(values.Departments));
                formData.append("UserId", editData.userId ?? editData.UserId ?? editData.id ?? editData.Id);

                const response = await axios.post(apiurl + "Users/update", formData, {
                    headers: {
                        "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`
                    }
                });
                // if(response.data === true) {
                Swal.fire({
                    ...swalApp,
                    icon: 'success',
                    title: t("users.update_success_title", "Utilisateur mis à jour"),
                    text: t("users.update_success_text", "L'utilisateur a été mis à jour avec succès."),
                    showConfirmButton: false,
                    timer: 2000
                });
                setLoading(false);
                setOpenModel(false);
                fetchUserList();
                // }
            } catch (error) {
                console.error("Error creating utilisateur:", error);
                Swal.fire({
                    ...swalApp,
                    icon: 'error',
                    title: t("users.update_error_title", "Erreur"),
                    text: backendValidationMessage(
                        error,
                        t("users.update_error_text", "Une erreur s'est produite lors de la mise à jour de l'utilisateur.")
                    ),
                });
                setLoading(false);
            }
            // console.log("Form submitted:", values);
            // alert(JSON.stringify(values, null, 2));
            setSubmitting(false);
            resetForm();
        },
    });

    const handleClickOpen = async (data) => {
        setLoading(true);
        try {
            const userId = data.userId ?? data.UserId ?? data.id ?? data.Id;
            const detail = await getManagedUserDetail(userId);
            const completeUser = { ...data, ...(detail || {}) };
            const editValues = normalizeUserEditValues(completeUser);

            setEdit(true);
            setEditData(completeUser);

            // ✅ Fetch departments only if there’s at least one province
            if (editValues.Provinces.length > 0) {
                await loadDepartments(editValues.Provinces);
            } else {
                setDeptOpts([]);
            }

            // Update values and clear validation/touched state from the previous user.
            editFormik.resetForm({ values: editValues });
            setOpenModel(true);
            window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to top
        } catch (error) {
            console.error("Error loading user details:", error);
            Swal.fire({
                ...swalApp,
                icon: "error",
                title: t("users.edit_load_error_title", "Unable to edit user"),
                text: apiErrorMessage(
                    error,
                    t("users.edit_load_error_text", "The complete user profile could not be loaded.")
                ),
            });
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async (userId) => {
        const confirmResult = await Swal.fire({
            ...swalDangerConfirm,
            title: t("users.delete_confirm_title", "Êtes-vous sûr?"),
            text: t("users.delete_confirm_text", "Cette action est irréversible!"),
            icon: 'warning',
            showCancelButton: true,
            reverseButtons: true,
            confirmButtonText: t("users.delete_confirm_ok", "Oui, supprimez-le!"),
            cancelButtonText: t("users.delete_confirm_cancel", "Annuler")
        });

        if (confirmResult.isConfirmed) {
            try {
                setLoading(true);
                const formData = new FormData();
                formData.append("UserId", userId);
                await axios.post(`${apiurl}Users/delete`, formData, {
                    headers: {
                        "Authorization": `Bearer ${localStorage.getItem("tk446655440000")}`
                    }
                });
                fetchUserList();
                setLoading(false);
                Swal.fire({
                    ...swalApp,
                    icon: 'success',
                    title: t("users.delete_success_title", "Supprimé!"),
                    text: t("users.delete_success_text", "L'utilisateur a été supprimé."),
                });
            } catch (error) {
                console.error("Error deleting user:", error);
                setLoading(false);
                Swal.fire({
                    ...swalApp,
                    icon: 'error',
                    title: t("users.delete_error_title", "Erreur"),
                    text: t("users.delete_error_text", "Une erreur s'est produite lors de la suppression de l'utilisateur."),
                });
            }
        }
    };

    const handleStatusChange = async (row) => {
        const isActive = accountIsActive(row);
        const nextActive = !isActive;
        if (!canChangeAccountStatus(row)) return;

        const result = await Swal.fire({
            ...swalDangerConfirm,
            title: nextActive
                ? t("users.activate_confirm_title", "Activate this account?")
                : t("users.deactivate_confirm_title", "Deactivate this account?"),
            text: nextActive
                ? t("users.activate_confirm_text", "The user will be able to sign in again.")
                : t("users.deactivate_confirm_text", "The user will immediately lose access."),
            input: "textarea",
            inputLabel: t("users.status_reason", "Reason (optional)"),
            inputPlaceholder: t("users.status_reason_placeholder", "Enter a reason"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: nextActive ? figmaTokens.accent : figmaTokens.badgeDanger,
            confirmButtonText: nextActive
                ? t("users.activate", "Activate")
                : t("users.deactivate", "Deactivate"),
            cancelButtonText: t("users.delete_confirm_cancel", "Cancel"),
        });
        if (!result.isConfirmed) return;

        try {
            setLoading(true);
            await setManagedUserStatus(row.userId ?? row.UserId ?? row.id ?? row.Id, nextActive, result.value?.trim() || "");
            await fetchUserList(page, statusFilter);
            Swal.fire({
                ...swalApp,
                icon: "success",
                title: nextActive
                    ? t("users.activate_success", "Account activated")
                    : t("users.deactivate_success", "Account deactivated"),
                timer: 1800,
                showConfirmButton: false,
            });
        } catch (error) {
            Swal.fire({
                ...swalApp,
                icon: "error",
                title: t("users.status_error_title", "Status update failed"),
                text: apiErrorMessage(error, t("users.status_error", "The account status could not be changed.")),
            });
        } finally {
            setLoading(false);
        }
    };



    const userList = Array.isArray(users) ? users : [];
    const totalPages = Math.ceil(totalRecords / rowsPerPage);
    const currentusers = userList;

    const handlePageChange = (event, value) => {
        setPage(value);
        fetchUserList(value, statusFilter);
    };

    const userRoleLabel = (row) => {
        const raw = getUserRowRole(row);
        const b = bucketUserRole(raw);
        switch (b) {
            case "admin":
                return t("users.role_admin", "Administrateur");
            case "member":
                return t("users.role_user", "Agent de saisie");
            case "observer":
                return t("users.role_observator", "Observateur");
            case "auth":
                return t("users.role_auth", "Autre utilisateur");
            default:
                return raw && String(raw).trim() ? String(raw) : t("users.role_other", "Other");
        }
    };

    const userListLen = totalRecords;
    const tableRangeLabel =
        userListLen === 0
            ? null
            : t("users.table_range", "{{start}}–{{end}} of {{total}} users")
                  .replace("{{start}}", String((page - 1) * rowsPerPage + 1))
                  .replace("{{end}}", String(Math.min(page * rowsPerPage, userListLen)))
                  .replace("{{total}}", String(userListLen));
    return (
        <DashboardLayout>
            {loading && <Loading />}
            <Dialog
                open={openModel}
                onClose={handleClose}
                maxWidth="lg"
                fullWidth
                PaperProps={usersDialogPaperProps}
            >
                <DialogTitle sx={{ p: 0 }}>
                    <SoftBox sx={{ height: 4, background: figmaTokens.gradientCSS }} />
                    <SoftBox sx={{ px: 2.5, py: 2.25, pr: 7, position: "relative" }}>
                        <SoftTypography
                            variant="overline"
                            sx={{
                                fontWeight: 800,
                                letterSpacing: "0.14em",
                                color: figmaTokens.textMuted,
                                fontSize: "0.68rem",
                            }}
                        >
                            {t("users.edit_kicker", "MODIFICATION")}
                        </SoftTypography>
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                            {t("users.edit_title", "Modifier l'utilisateur")}
                        </SoftTypography>
                        <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
                            {t("users.edit_hint", "Mettez à jour le profil et la zone d'intervention.")}
                        </SoftTypography>
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                            sx={{
                                position: "absolute",
                                right: 10,
                                top: 14,
                                color: figmaTokens.textSecondary,
                                "&:hover": { color: figmaTokens.textPrimary, bgcolor: "rgba(15, 23, 42, 0.06)" },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </SoftBox>
                </DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 1, pt: 1 }}>
                    <form id="edit-user-form" onSubmit={editFormik.handleSubmit} className="assignment-form">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.first_name", "Prénoms")}
                                </SoftTypography>
                                <SoftInput type="text" name="firstName" value={editFormik.values.firstName} onChange={editFormik.handleChange} onBlur={editFormik.handleBlur} />
                                {editFormik.touched.firstName && editFormik.errors.firstName ? (
                                    <div className="error-message">
                                        {editFormik.errors.firstName}
                                    </div>
                                ) : null}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.last_name", "Nom de famille")}
                                </SoftTypography>
                                <SoftInput type="text" name="lastName" value={editFormik.values.lastName} onChange={editFormik.handleChange} onBlur={editFormik.handleBlur} />
                                {editFormik.touched.lastName && editFormik.errors.lastName ? (
                                    <div className="error-message">
                                        {editFormik.errors.lastName}
                                    </div>
                                ) : null}
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.title", "Titre")}
                                </SoftTypography>
                                <SoftInput
                                    type="text"
                                    name="title"
                                    value={editFormik.values.title}
                                    onChange={editFormik.handleChange}
                                    onBlur={editFormik.handleBlur}
                                    placeholder={t("users.title_placeholder", "Ex. Recteur, Inspecteur")}
                                    inputProps={{ maxLength: 100 }}
                                />
                                {editFormik.touched.title && editFormik.errors.title ? (
                                    <div className="error-message">{editFormik.errors.title}</div>
                                ) : null}
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.gender_label", "Sélectionnez le sexe")}
                                </SoftTypography>
                                <Select2Field
                                    name="gender"
                                    options={[
                                        { id: "", text: t("users.gender_placeholder", "Sélectionner le sexe") },
                                        { id: "male", text: t("gender.male", "Homme") },
                                        { id: "female", text: t("gender.female", "Femme") },
                                    ]}
                                    placeholder={t("users.gender_placeholder", "Sélectionner le sexe")}
                                    value={editFormik.values.gender}
                                    onChange={(val) => {
                                        editFormik.setFieldValue("gender", val);
                                    }}
                                />
                                {editFormik.errors.gender && editFormik.touched.gender && (
                                    <div style={{ color: "red", fontSize: 12 }}>{editFormik.errors.gender}</div>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.phone", "Téléphone")}
                                </SoftTypography>
                                <SoftInput type="tel" name="phone" value={editFormik.values.phone} onChange={editFormik.handleChange} onBlur={editFormik.handleBlur} />
                                {editFormik.touched.phone && editFormik.errors.phone ? (
                                    <div className="error-message">
                                        {editFormik.errors.phone}
                                    </div>
                                ) : null}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.whatsapp", "WhatsApp")}
                                </SoftTypography>
                                <SoftInput type="tel" name="whastappNo" value={editFormik.values.whastappNo} onChange={editFormik.handleChange} onBlur={editFormik.handleBlur} />
                                {editFormik.touched.whastappNo && editFormik.errors.whastappNo ? (
                                    <div className="error-message">
                                        {editFormik.errors.whastappNo}
                                    </div>
                                ) : null}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.province", "Province")}
                                </SoftTypography>
                                <Select2Field
                                    name="Provinces"
                                    options={[
                                        ...provOpts,
                                    ]}
                                    placeholder={t("users.province_placeholder", "Sélectionner province")}
                                    value={editFormik.values.Provinces}
                                    onChange={(provid) => {
                                        let provinceValue = "";
                                        if (Array.isArray(provid)) {
                                            provinceValue = provid.join(",");
                                        } else {
                                            provinceValue = provid;
                                        }
                                        editFormik.setFieldValue("Provinces", provinceValue);
                                        editFormik.setFieldValue("Departments", "");
                                        console.log("Selected province ID:", provinceValue);
                                        setDeptOpts([]);
                                        fetchDepartments(provinceValue);
                                    }}
                                    multiple={true}
                                    showCheckboxes={true}
                                />
                                {editFormik.errors.Provinces && editFormik.touched.Provinces && (
                                    <div style={{ color: "red", fontSize: 12 }}>{editFormik.errors.Provinces}</div>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <SoftTypography component="label" sx={userEditFieldLabelSx}>
                                    {t("users.department", "Département")}
                                </SoftTypography>
                                <Select2Field
                                    name="Departments"
                                    options={[
                                        ...deptOpts
                                    ]}
                                    placeholder={t("users.department_placeholder", "Sélectionner Département")}
                                    disabled={
                                        !editFormik?.values?.Provinces
                                    }
                                    value={editFormik.values.Departments}
                                    onChange={(dptid) => {
                                        let dptValue = "";
                                        if (Array.isArray(dptid)) {
                                            dptValue = dptid.join(",");
                                        } else {
                                            dptValue = dptid;
                                        }
                                        editFormik.setFieldValue("Departments", dptValue);
                                    }}
                                    multiple={true}
                                    showCheckboxes={true}
                                />
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: 2.5,
                        py: 2,
                        gap: 1,
                        borderTop: `1px solid ${figmaTokens.cardHeaderRule}`,
                        bgcolor: figmaTokens.pageBg,
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "12px",
                            px: 2.5,
                            borderColor: figmaTokens.borderStrong,
                            color: figmaTokens.textSecondary,
                        }}
                    >
                        {t("users.cancel", "Annuler")}
                    </Button>
                    <Button
                        type="submit"
                        form="edit-user-form"
                        variant="contained"
                        disableElevation
                        disabled={editFormik.isSubmitting}
                        sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            borderRadius: "12px",
                            px: 2.75,
                            py: 1,
                            color: "#fff",
                            background: figmaTokens.gradientCSS,
                            boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                            "&:hover": {
                                filter: "brightness(1.06)",
                                boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                            },
                        }}
                    >
                        {editFormik.isSubmitting ? t("users.saving", "Enregistrement...") : t("users.save", "ENREGISTRER")}
                    </Button>
                </DialogActions>
            </Dialog>
            {/* <Loading /> */}
            <DashboardNavbar />
            <SoftBox py={3} sx={{ bgcolor: figmaTokens.pageBg }} className="content-area-main">
                <SoftBox mb={3}>
                    <Card
                        sx={{
                            borderRadius: "18px",
                            overflow: "hidden",
                            border: "none",
                            boxShadow: "0 22px 56px rgba(15, 23, 42, 0.09)",
                        }}
                    >
                        <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
                        <SoftBox sx={{ p: { xs: 2.25, md: 3 } }}>
                            <SoftBox sx={{ minWidth: 0, flex: "1 1 240px" }}>
                                <SoftTypography
                                    variant="overline"
                                    sx={{
                                        fontWeight: 800,
                                        letterSpacing: "0.14em",
                                        color: figmaTokens.textMuted,
                                        fontSize: "0.68rem",
                                    }}
                                >
                                    {t("users.page_kicker", "USERS")}
                                </SoftTypography>
                                <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
                                    {t("users.page_title", "User management")}
                                </SoftTypography>
                                <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 560 }}>
                                    {t(
                                        "users.page_hint",
                                        "Create accounts and manage access by role and area."
                                    )}
                                </SoftTypography>
                            </SoftBox>
                        </SoftBox>
                    </Card>
                </SoftBox>

                <SoftBox
                    sx={{
                        display: "grid",
                        gap: 2,
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            sm: "repeat(3, minmax(0, 1fr))",
                        },
                        mb: 3,
                    }}
                >
                    <SoftBox sx={usersStatWrapSx}>
                        <MiniStatisticsCard
                            title={{ text: t("users.stats.total", "Total") }}
                            count={summary.total}
                            percentage={{ color: "success", text: "" }}
                            icon={{ color: "info", component: "groups" }}
                        />
                    </SoftBox>
                    <SoftBox sx={usersStatWrapSx}>
                        <MiniStatisticsCard
                            title={{ text: t("users.stats.active", "Active") }}
                            count={summary.active}
                            percentage={{ color: "success", text: "" }}
                            icon={{ color: "success", component: "check_circle" }}
                        />
                    </SoftBox>
                    <SoftBox sx={usersStatWrapSx}>
                        <MiniStatisticsCard
                            title={{ text: t("users.stats.inactive", "Deactivated") }}
                            count={summary.inactive}
                            percentage={{ color: "success", text: "" }}
                            icon={{ color: "error", component: "block" }}
                        />
                    </SoftBox>
                </SoftBox>

                <Card
                    variant="outlined"
                    sx={{
                        mb: 3,
                        borderRadius: "16px",
                        border: `1px solid ${figmaTokens.borderSubtle}`,
                        boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
                        bgcolor: figmaTokens.surface,
                        overflow: "hidden",
                    }}
                >
                    <SoftBox
                        sx={{
                            px: { xs: 2, md: 2.5 },
                            py: 2,
                            borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 1.5,
                        }}
                    >
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                            {t("users.form_section_title", "New user")}
                        </SoftTypography>
                        <Button
                            variant={formOpen ? "outlined" : "contained"}
                            disableElevation={!formOpen}
                            onClick={() => setFormOpen((prev) => !prev)}
                            sx={
                                formOpen
                                    ? {
                                          textTransform: "none",
                                          fontWeight: 700,
                                          px: 2.5,
                                          py: 1,
                                          borderRadius: "12px",
                                          borderColor: figmaTokens.borderStrong,
                                          color: figmaTokens.textSecondary,
                                      }
                                    : {
                                          textTransform: "none",
                                          fontWeight: 700,
                                          px: 2.75,
                                          py: 1,
                                          borderRadius: "12px",
                                          color: "#fff",
                                          background: figmaTokens.gradientCSS,
                                          boxShadow: "0 10px 26px rgba(102, 126, 234, 0.35)",
                                          "&:hover": {
                                              filter: "brightness(1.06)",
                                              boxShadow: "0 14px 32px rgba(102, 126, 234, 0.42)",
                                          },
                                      }
                            }
                        >
                            {formOpen
                                ? t("users.form_hide", "Hide form")
                                : t("users.form_add", "Add user")}
                        </Button>
                    </SoftBox>
                    <Collapse in={formOpen} timeout="auto" unmountOnExit>
                        <form onSubmit={formik.handleSubmit} className="assignment-form">
                            <Grid container spacing={1}>
                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.first_name", "Prénoms")}
                                    </SoftTypography>
                                    <SoftInput type="text" name="firstName" value={formik.values.firstName} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.firstName && formik.errors.firstName ? (
                                        <div className="error-message">
                                            {formik.errors.firstName}
                                        </div>
                                    ) : null}
                                </Grid>
                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.last_name", "Nom de famille")}
                                    </SoftTypography>
                                    <SoftInput type="text" name="lastName" value={formik.values.lastName} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.lastName && formik.errors.lastName ? (
                                        <div className="error-message">
                                            {formik.errors.lastName}
                                        </div>
                                    ) : null}
                                </Grid>

                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.title", "Titre")}
                                    </SoftTypography>
                                    <SoftInput
                                        type="text"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={t("users.title_placeholder", "Ex. Recteur, Inspecteur")}
                                        inputProps={{ maxLength: 100 }}
                                    />
                                    {formik.touched.title && formik.errors.title ? (
                                        <div className="error-message">{formik.errors.title}</div>
                                    ) : null}
                                </Grid>

                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.email", "E-mail")}
                                    </SoftTypography>
                                    <SoftInput type="email" name="email" value={formik.values.email} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.email && formik.errors.email ? (
                                        <div className="error-message">
                                            {formik.errors.email}
                                        </div>
                                    ) : null}
                                </Grid>

                                <Grid item xs={12} xl={3}>
                                    <label>{t("users.gender_label", "Sélectionnez le sexe")}</label>
                                    <Select2Field
                                        name="gender"
                                        options={[
                                            { id: "", text: t("users.gender_placeholder", "Sélectionner le sexe") },
                                            { id: "male", text: t("gender.male", "Homme") },
                                            { id: "female", text: t("gender.female", "Femme") },
                                        ]}
                                        placeholder={t("users.gender_placeholder", "Sélectionner le sexe")}
                                        value={formik.values.gender}
                                        onChange={(val) => {
                                            formik.setFieldValue("gender", val);
                                        }}
                                    />
                                    {formik.errors.gender && formik.touched.gender && (
                                        <div style={{ color: "red", fontSize: 12 }}>{formik.errors.gender}</div>
                                    )}
                                </Grid>

                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.password", "Mot de passe")}
                                    </SoftTypography>
                                    <SoftInput type="password" name="password" value={formik.values.password} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.password && formik.errors.password ? (
                                        <div className="error-message">
                                            {formik.errors.password}
                                        </div>
                                    ) : null}
                                </Grid>
                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.confirm_password", "Confirmez le mot de passe")}
                                    </SoftTypography>
                                    <SoftInput type="password" name="confirmPassword" value={formik.values.confirmPassword} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.confirmPassword && formik.errors.confirmPassword ? (
                                        <div className="error-message">
                                            {formik.errors.confirmPassword}
                                        </div>
                                    ) : null}
                                </Grid>
                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.phone", "Téléphone")}
                                    </SoftTypography>
                                    <SoftInput type="tel" name="phone" value={formik.values.phone} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.phone && formik.errors.phone ? (
                                        <div className="error-message">
                                            {formik.errors.phone}
                                        </div>
                                    ) : null}
                                </Grid>
                                <Grid item xs={12} xl={3}>
                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                        {t("users.whatsapp", "WhatsApp")}
                                    </SoftTypography>
                                    <SoftInput type="tel" name="whastappNo" value={formik.values.whastappNo} onChange={formik.handleChange} onBlur={formik.handleBlur} />
                                    {formik.touched.whastappNo && formik.errors.whastappNo ? (
                                        <div className="error-message">
                                            {formik.errors.whastappNo}
                                        </div>
                                    ) : null}
                                </Grid>



                                <Grid item xs={12} xl={3}>
                                    <label>{t("users.role_label", "Sélectionner un rôle")}</label>
                                    <Select2Field
                                        name="role"
                                        options={[
                                            { id: "", text: t("users.role_placeholder", "Sélectionner un rôle") },
                                            ...roleOptions,
                                        ]}
                                        placeholder={t("users.role_placeholder", "Sélectionner un rôle")}
                                        value={formik.values.role}
                                        onChange={(val) => {
                                            formik.setFieldValue("role", val);
                                        }}
                                    />
                                    {formik.errors.role && formik.touched.role && (
                                        <div style={{ color: "red", fontSize: 12 }}>{formik.errors.role}</div>
                                    )}
                                    {rolesError && (
                                        <div style={{ color: "red", fontSize: 12 }}>{rolesError}</div>
                                    )}
                                </Grid>

                                <Grid item xs={12} xl={3}>
                                    <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                                        <label>{t("users.province", "Province")}</label>
                                        <Button
                                            type="button"
                                            size="small"
                                            variant="text"
                                            disabled={!provOpts.length || loading}
                                            onClick={selectAllCreateGeography}
                                            sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 700, p: 0, minWidth: 0 }}
                                        >
                                            {t("users.select_all_geography", "Select all provinces and departments")}
                                        </Button>
                                    </SoftBox>
                                    <Select2Field
                                        name="Provinces"
                                        options={[
                                            ...provOpts,
                                        ]}
                                        placeholder={t("users.province_placeholder", "Sélectionner province")}
                                        value={formik.values.Provinces}
                                        onChange={(provid) => {
                                            const provinceValue = Array.isArray(provid)
                                                ? provid
                                                : normalizeIdCsv(provid).split(",").filter(Boolean);
                                            formik.setFieldValue("Provinces", provinceValue);
                                            formik.setFieldValue("Departments", []);
                                            console.log("Selected province ID:", provinceValue);
                                            setDeptOpts([]);
                                            fetchDepartments(provinceValue);
                                        }}
                                        multiple={true}
                                        showCheckboxes={true}
                                    />
                                    {formik.errors.Provinces && formik.touched.Provinces && (
                                        <div style={{ color: "red", fontSize: 12 }}>{formik.errors.Provinces}</div>
                                    )}
                                </Grid>
                                <Grid item xs={12} xl={3}>
                                    <SoftBox display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={0.5}>
                                        <label>{t("users.department", "Département")}</label>
                                        <Button
                                            type="button"
                                            size="small"
                                            variant="text"
                                            disabled={!deptOpts.length || loading}
                                            onClick={selectAllCreateDepartments}
                                            sx={{ textTransform: "none", fontSize: "0.72rem", fontWeight: 700, p: 0, minWidth: 0 }}
                                        >
                                            {t("users.select_all_departments", "Select all departments")}
                                        </Button>
                                    </SoftBox>
                                    <Select2Field
                                        name="Departments"
                                        options={[
                                            ...deptOpts
                                        ]}
                                        placeholder={t("users.department_placeholder", "Sélectionner Département")}
                                        disabled={!formik.values.Provinces.length}
                                        value={formik.values.Departments}
                                        onChange={(dptid) => {
                                            const dptValue = Array.isArray(dptid)
                                                ? dptid
                                                : normalizeIdCsv(dptid).split(",").filter(Boolean);
                                            formik.setFieldValue("Departments", dptValue);
                                        }}
                                        multiple={true}
                                        showCheckboxes={true}
                                    />
                                    {formik.errors.Departments && formik.touched.Departments && (
                                        <div style={{ color: "red", fontSize: 12 }}>{formik.errors.Departments}</div>
                                    )}
                                </Grid>

                                <Grid item xs={12} xl={3}>
                                    <SoftButton type="submit" variant="gradient" color="info" fullWidth style={{ marginTop: '30px' }}>
                                        {formik.isSubmitting ? "Loading..." : t("users.save", "ENREGISTRER")}
                                    </SoftButton>
                                </Grid>
                            </Grid>
                        </form>
                    </Collapse>
                </Card>

                <Card
                    variant="outlined"
                    sx={{
                        borderRadius: "16px",
                        border: `1px solid ${figmaTokens.borderSubtle}`,
                        boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
                        overflow: "hidden",
                        bgcolor: figmaTokens.surface,
                    }}
                >
                    <SoftBox
                        sx={{
                            px: { xs: 2, md: 2.5 },
                            py: 2,
                            borderBottom: `1px solid ${figmaTokens.cardHeaderRule}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexWrap: "wrap",
                            gap: 1.5,
                        }}
                    >
                        <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
                            {t("users.table_section_title", "User directory")}
                        </SoftTypography>
                        <AccountStatusTabs
                            value={statusFilter}
                            onChange={setStatusFilter}
                            counts={{ all: summary.total, active: summary.active, inactive: summary.inactive }}
                            labels={{
                                all: t("users.status_all", "All"),
                                active: t("users.status_active", "Active"),
                                inactive: t("users.status_inactive", "Deactivated"),
                            }}
                        />
                    </SoftBox>
                    <SoftBox sx={{ px: { xs: 1.25, md: 2 }, pt: 2, pb: 2 }}>
                        {tableRangeLabel && (
                            <SoftTypography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mb: 1.5,
                                    fontWeight: 700,
                                    letterSpacing: "0.06em",
                                    color: figmaTokens.textMuted,
                                    textTransform: "uppercase",
                                }}
                            >
                                {tableRangeLabel}
                            </SoftTypography>
                        )}
                        <TableContainer component={Paper} elevation={0} sx={usersTableShellSx}>
                            <Table
                                size="small"
                                stickyHeader
                                sx={{
                                    minWidth: 1080,
                                    ...directoryTableHeadStickyOverridesSx,
                                    ...directoryTableBodyCellDefaultsSx,
                                    "& .MuiTableRow-root:hover .MuiTableCell-root": {
                                        bgcolor: "rgba(102, 126, 234, 0.045)",
                                    },
                                }}
                                aria-label={t("users.list_title", "Liste des utilisateurs")}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>{t("users.table.name", "Nom")}</TableCell>
                                        <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>{t("users.table.title", "Titre")}</TableCell>
                                        <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 200 }}>{t("users.table.email", "E-mail")}</TableCell>
                                        <TableCell sx={directoryTableHeaderCellSx}>{t("users.table.gender", "Sexe")}</TableCell>
                                        <TableCell sx={{ ...directoryTableHeaderCellSx, minWidth: 140 }}>{t("users.table.role", "Rôle")}</TableCell>
                                        <TableCell sx={directoryTableHeaderCellSx}>{t("users.table.status", "Status")}</TableCell>
                                        <TableCell sx={directoryTableHeaderCellSx}>{t("users.table.phone", "Téléphone")}</TableCell>
                                        <TableCell sx={directoryTableHeaderCellSx}>{t("users.table.whatsapp", "WhatsApp")}</TableCell>
                                        <TableCell sx={{ ...directoryTableHeaderCellSx, maxWidth: 200 }}>{t("users.table.provinces", "Provinces")}</TableCell>
                                        <TableCell sx={{ ...directoryTableHeaderCellSx, maxWidth: 180 }}>{t("users.table.department", "Département")}</TableCell>
                                        <TableCell align="right" sx={directoryTableHeaderCellSx}>{t("users.table.actions", "Actes")}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentusers.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={11} align="center" sx={{ py: 6, color: figmaTokens.textSecondary, fontSize: "0.9rem" }}>
                                                {t("users.table_empty", "No users to display.")}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {currentusers.map((row) => (
                                        <TableRow
                                            key={row.id ?? row.userId}
                                            hover
                                            sx={{
                                                "&:nth-of-type(even) td": { bgcolor: "rgba(247, 249, 252, 0.65)" },
                                                transition: "background-color 0.15s ease",
                                            }}
                                        >
                                            <TableCell sx={{ fontWeight: 700, color: figmaTokens.textPrimary }}>{row.fullName ?? "--"}</TableCell>
                                            <TableCell sx={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }} title={row.title ?? row.Title ?? ""}>
                                                {row.title ?? row.Title ?? "--"}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }} title={row.email}>
                                                {row.email ?? "--"}
                                            </TableCell>
                                            <TableCell sx={{ color: figmaTokens.textBody }}>{row.gender ?? "--"}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={userRoleLabel(row)}
                                                    size="small"
                                                    sx={userRoleChipSx(bucketUserRole(getUserRowRole(row)))}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={accountIsActive(row) ? t("users.status_active", "Active") : t("users.status_inactive", "Deactivated")}
                                                    size="small"
                                                    color={accountIsActive(row) ? "success" : "default"}
                                                    variant={accountIsActive(row) ? "filled" : "outlined"}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{row.phoneNumber ?? "--"}</TableCell>
                                            <TableCell sx={{ whiteSpace: "nowrap" }}>{row.whastappNo ?? row.WhastappNo ?? "--"}</TableCell>
                                            <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }} title={row.provinceNames}>
                                                {row.provinceNames ?? "--"}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }} title={row.departmentNames}>
                                                {row.departmentNames ?? "--"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <SoftBox display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleClickOpen(row)}
                                                        sx={{
                                                            color: figmaTokens.textSecondary,
                                                            "&:hover": { color: figmaTokens.accent, bgcolor: "rgba(102, 126, 234, 0.1)" },
                                                        }}
                                                    >
                                                        <Icon fontSize="small">edit</Icon>
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(row.userId)}
                                                        sx={{
                                                            color: figmaTokens.textSecondary,
                                                            "&:hover": { color: "#ef4444", bgcolor: "rgba(239, 68, 68, 0.1)" },
                                                        }}
                                                    >
                                                        <Icon fontSize="small">delete</Icon>
                                                    </IconButton>
                                                    {canChangeAccountStatus(row) && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleStatusChange(row)}
                                                            title={accountIsActive(row) ? t("users.deactivate", "Deactivate") : t("users.activate", "Activate")}
                                                            sx={{
                                                                color: accountIsActive(row) ? "#dc2626" : "#059669",
                                                                "&:hover": { bgcolor: accountIsActive(row) ? "rgba(220, 38, 38, 0.1)" : "rgba(5, 150, 105, 0.1)" },
                                                            }}
                                                        >
                                                            <Icon fontSize="small">{accountIsActive(row) ? "block" : "check_circle"}</Icon>
                                                        </IconButton>
                                                    )}
                                                </SoftBox>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {totalRecords > rowsPerPage && (
                            <Stack spacing={2} alignItems="center" sx={{ py: 2.5, borderTop: `1px solid ${figmaTokens.cardHeaderRule}` }}>
                                <Pagination
                                    count={totalPages}
                                    page={page}
                                    onChange={handlePageChange}
                                    color="primary"
                                    shape="rounded"
                                />
                            </Stack>
                        )}
                    </SoftBox>
                </Card>
            </SoftBox>
            <Footer />
        </DashboardLayout>
    );
}

export default Register;
